/**
 * Which NFL seasons exist, which one is being played now, and how the
 * weeks of one season are arranged.
 *
 * Everything here comes from ESPN. Nothing is derived from the system
 * clock and no year is written down in this file except the floor below,
 * which is a documented limit of the data rather than a guess about the
 * calendar. When ESPN publishes the 2027 season it appears in the picker
 * on its own, because the list is the list ESPN returns.
 *
 * TWO HOSTS, TWO JOBS
 * -------------------
 *   sports.core.api.espn.com  the reference data API. Says which seasons
 *                             exist and which one the league is in. Serves
 *                             CORS to a browser and is already named in
 *                             the connect-src of the CSP in firebase.json.
 *   cdn.espn.com              the schedule host `espnProvider` already
 *                             uses. Every schedule payload carries a
 *                             `calendar` for the season it belongs to,
 *                             which is where the week structure and the
 *                             week names come from.
 *
 * WHY THE WEEK NAMES ARE NOT WRITTEN DOWN HERE
 * --------------------------------------------
 * They cannot be. The shape of a season moves: the 2015 regular season had
 * 17 weeks and five preseason weeks, the 2025 one had 18 and four. The
 * postseason rounds are named "Wild Card", "Divisional Round",
 * "Conference Championship", "Pro Bowl" and "Super Bowl" in the payload
 * itself, so nothing in this product has to know what a Super Bowl is in
 * order to label one. Verified against captured 2015, 2025 and 2026
 * calendars.
 *
 * THE FLOOR, AND WHY IT IS 2001
 * -----------------------------
 * The season list ESPN publishes runs back to 1927, and offering all of it
 * would be a lie by omission. This product's whole purpose is explaining a
 * game while you watch it, and that needs play-by-play. Probing
 * `cdn.espn.com/core/nfl/game?xhr=1&gameId=...` for the opening games of
 * each season, on 2026-08-12:
 *
 *   2004 week 1   29, 27 and 26 drives
 *   2002 week 1   28, 35 and 34 drives
 *   2001 week 1   32, 29 and 28 drives; week 10 and the postseason too
 *   2000 week 1   0 drives. Week 10 as well. Boxscore present, plays gone.
 *   1998          the schedule endpoint does not return JSON at all
 *
 * So 2001 is the oldest season where a game can still be explained play by
 * play, and it is the floor. A 2000 game would open as a scoreline with an
 * empty play feed, which is exactly the hollow experience this product
 * refuses to ship. Some individual 2003 games carry only a partial feed
 * (two of the three probed had four and five drives), so the floor is a
 * promise about the season being browsable, not about every single game
 * being complete.
 */

import { fetchJson } from "../../utils/httpClient";

/**
 * ESPN's reference data API. Overridable for the same reason
 * `espnProvider` makes its host overridable: so a future licensed feed is
 * a configuration change rather than a code change.
 */
const CORE_BASE_URL =
  String(process.env.REACT_APP_SPORTS_CORE_API_BASE_URL ?? "").trim() ||
  "https://sports.core.api.espn.com/v2/sports/football/leagues/nfl";

/** The oldest season with play-by-play. See the note at the top. */
export const SEASON_FLOOR_YEAR = 2001;

/**
 * ESPN's season phases, by the id they carry in every payload. Confirmed
 * against `.../seasons/2026/types`, which returns exactly ids 1 to 4.
 *
 * Only the id of the off season is named here, and only so it can be left
 * out of a week picker: it is a five month gap with no fixtures in it, and
 * offering "Off Season, week 1" as somewhere to look would be nonsense.
 * Every label a reader sees still comes from the payload.
 */
export const OFF_SEASON_TYPE_ID = 4;

/** The regular season, which is where browsing a past season starts. */
export const REGULAR_SEASON_TYPE_ID = 2;

/**
 * A season list request is not on the critical path of anything: the
 * schedule paints without it. Short timeout, one retry, then give up and
 * let the picker stay hidden.
 */
const REQUEST = Object.freeze({ retries: 1, timeoutMs: 8000 });

/** Years live in the `$ref` URL rather than in a field of their own. */
export function yearFromSeasonRef(ref) {
  const match = String(ref ?? "").match(/\/seasons\/(\d{4})/);
  return match ? Number(match[1]) : null;
}

/**
 * The seasons ESPN has, newest first, floored.
 *
 * `?limit=100` returns the first page of 105, which is 2026 back to 1927.
 * A second page exists and is deliberately not fetched: everything on it
 * predates the floor by seventy years.
 */
export async function fetchSeasonYears() {
  const payload = await fetchJson(
    `${CORE_BASE_URL}/seasons?limit=100`,
    REQUEST
  );

  const years = (payload?.items ?? [])
    .map((item) => yearFromSeasonRef(item?.$ref))
    .filter((year) => Number.isInteger(year) && year >= SEASON_FLOOR_YEAR);

  return [...new Set(years)].sort((a, b) => b - a);
}

/**
 * The season the league is in right now, straight from ESPN.
 *
 * The league resource carries the whole current season inline: year,
 * displayName, the real start and end dates, and the active phase with the
 * week it is in. One request, no clock arithmetic, and it rolls over on
 * ESPN's schedule rather than on ours.
 *
 * The phase is read and returned because it is the proof this is the
 * ACTIVE season rather than the newest row in a table. Nothing renders it:
 * a phase name is the one field here that goes stale inside a day, and the
 * schedule on screen already says whether it is preseason or not.
 */
export async function fetchCurrentSeason() {
  const payload = await fetchJson(CORE_BASE_URL, REQUEST);
  const season = payload?.season;
  const year = Number(season?.year);

  if (!Number.isInteger(year)) return null;

  return {
    year,
    displayName: String(season.displayName ?? year),
    startDate: season.startDate ?? null,
    endDate: season.endDate ?? null,
    phase: {
      id: Number(season.type?.id) || null,
      name: season.type?.name ?? null,
      week: Number(season.type?.week?.number) || null,
    },
  };
}

/**
 * The catalogue: what a picker needs, in one shape.
 *
 * Both requests have to succeed. A list of seasons with no idea which one
 * is current cannot be offered to anyone, because "current" is the default
 * and the only entry whose data is live. When this rejects, the caller
 * hides the picker and the app carries on exactly as it did before there
 * was one.
 *
 * The current season is unioned into the list rather than assumed to be on
 * it, so the day ESPN's league resource moves on before its season index
 * does, the picker still offers the season being played.
 */
export async function fetchSeasonCatalogue() {
  const [years, current] = await Promise.all([
    fetchSeasonYears(),
    fetchCurrentSeason(),
  ]);

  if (!current) {
    throw new Error("ESPN did not say which NFL season is being played.");
  }

  const merged = [...new Set([current.year, ...years])]
    .filter((year) => year >= SEASON_FLOOR_YEAR)
    .sort((a, b) => b - a);

  return { years: merged, current, floorYear: SEASON_FLOOR_YEAR };
}

/* ------------------------------------------------------- one season */

/**
 * ESPN's calendar for one season, as weeks a person can navigate.
 *
 * The payload groups weeks by phase. Each group carries the phase's own id
 * and label, and each entry carries the week number, its name and the
 * dates it covers. Empty groups and the off season are dropped, because
 * neither has a fixture in it.
 *
 * @param {Array} calendar `content.calendar` from a schedule payload
 */
export function normalizeSeasonCalendar(calendar) {
  return (Array.isArray(calendar) ? calendar : [])
    .map((group) => ({
      seasonTypeId: Number(group?.value),
      label: String(group?.label ?? ""),
      startDate: group?.startDate ?? null,
      endDate: group?.endDate ?? null,
      weeks: (group?.entries ?? [])
        .map((entry) => ({
          number: Number(entry?.value),
          label: String(entry?.label ?? ""),
          shortLabel: entry?.alternateLabel ?? null,
          detail: entry?.detail ?? null,
        }))
        .filter((week) => Number.isInteger(week.number) && week.label !== ""),
    }))
    .filter(
      (group) =>
        Number.isInteger(group.seasonTypeId) &&
        group.seasonTypeId !== OFF_SEASON_TYPE_ID &&
        group.label !== "" &&
        group.weeks.length > 0
    );
}

/**
 * When a season ran, taken from its own calendar.
 *
 * The first phase's start and the last phase's end are the season's real
 * span, and they agree with the dates the league resource gives for the
 * current season: the 2026 calendar says 2026-08-06 to 2027-02-16, which
 * is exactly what `.../leagues/nfl` reports. That agreement is why one
 * source can be used for the season being played and the other for a
 * season that has finished.
 */
export function seasonSpanOf(phases) {
  if (!Array.isArray(phases) || phases.length === 0) return null;

  return {
    startDate: phases[0].startDate ?? null,
    endDate: phases[phases.length - 1].endDate ?? null,
  };
}

/**
 * The week structure itself is fetched by `espnProvider.getSeasonWeeks`,
 * not here, so that it shares one cached response with the fixtures for
 * the same week. This module keeps the two pure functions above, which is
 * what stops that arrangement becoming a circular import.
 */

const espnSeasonProvider = Object.freeze({
  id: "nfl",
  isSimulated: false,
  floorYear: SEASON_FLOOR_YEAR,

  fetchSeasonCatalogue,
});

export default espnSeasonProvider;
